import type { CurriculumLesson, CurriculumMap } from "./types";

export const JAPANESE_KANA_CURRICULUM_ID = "curriculum-japanese-a1-foundations";
export const BASICS_CURRICULUM_ID = "curriculum-english-basics";

export function isBasicsCurriculumMap(map: CurriculumMap): boolean {
  return map.curriculum.id === BASICS_CURRICULUM_ID;
}

export function isKanaCurriculumMap(map: CurriculumMap): boolean {
  return map.curriculum.id === JAPANESE_KANA_CURRICULUM_ID
    || map.lessons.some((item) => item.lesson.lessonKind === "kana");
}

export function sortCurriculumLessons(lessons: CurriculumLesson[]): CurriculumLesson[] {
  return [...lessons].sort((a, b) => a.sortOrder - b.sortOrder || a.lesson.title.localeCompare(b.lesson.title));
}

export function groupCurriculumLessonsByCategory(map: CurriculumMap): Array<{ categoryId?: string; categoryTitle: string; sortOrder: number; lessons: CurriculumLesson[] }> {
  const groups = new Map<string, CurriculumLesson[]>();
  for (const lesson of sortCurriculumLessons(map.lessons)) {
    const categoryTitle = lesson.categoryTitle || "Core Lessons";
    groups.set(categoryTitle, [...(groups.get(categoryTitle) ?? []), lesson]);
  }
  if (!map.categories?.length) {
    return [...groups.entries()].map(([categoryTitle, lessons], index) => ({ categoryTitle, sortOrder: index + 1, lessons }));
  }

  const categories = [...map.categories].sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  const categoryTitles = new Set(categories.map((category) => category.title));
  const grouped: Array<{ categoryId?: string; categoryTitle: string; sortOrder: number; lessons: CurriculumLesson[] }> = categories.map((category) => ({
    categoryId: category.id,
    categoryTitle: category.title,
    sortOrder: category.sortOrder,
    lessons: groups.get(category.title) ?? []
  }));

  for (const [categoryTitle, lessons] of groups.entries()) {
    if (!categoryTitles.has(categoryTitle)) {
      grouped.push({ categoryTitle, sortOrder: grouped.length + 1, lessons });
    }
  }

  return grouped;
}

export function publishedCurriculumMap(map: CurriculumMap | null): CurriculumMap | null {
  if (!map) return null;
  return {
    ...map,
    lessons: map.lessons.filter((item) => item.lesson.status === "ready")
  };
}

export function publishedLessonNumberMap(map: CurriculumMap | null): Map<string, number> {
  const lessonNumbers = new Map<string, number>();
  if (!map) return lessonNumbers;
  for (const category of groupCurriculumLessonsByCategory(map)) {
    category.lessons
      .filter((item) => item.lesson.status === "ready")
      .forEach((item, index) => lessonNumbers.set(item.lessonId, index + 1));
  }
  return lessonNumbers;
}

export function audioReadinessLabel(lesson: CurriculumLesson): string {
  const readyScopes = new Set(lesson.lesson.assets.filter((asset) => asset.asset.assetType === "audio").map((asset) => asset.scope));
  const readyCount = lesson.requiredAudioScopes.filter((scope) => readyScopes.has(scope)).length;
  return `${readyCount}/${lesson.requiredAudioScopes.length} audio`;
}
